import React, { useContext } from 'react';

import ClientContext from 'app/src/contexts/ClientContext';
import { Routes, Route, useSearchParams } from 'app/src/utils/routing';

import Home from 'app/src/components/Home';

import AccessTokens from 'app/src/components/AccessTokens';

import Charts from 'app/src/components/Charts';

import Dealers from 'app/src/components/Dealers';
import Dealer from 'app/src/components/Dealers/show';
import NewDealer from 'app/src/components/Dealers/new';
import EditDealer from 'app/src/components/Dealers/edit';
import DealersNonAdrPreRegistrations from 'app/src/components/Dealers/NonAdrPreRegistrations';
import DealersNoRecentOrder from 'app/src/components/Dealers/NoRecentOrder';
import DealersNameDiffersLastOrder from 'app/src/components/Dealers/NameDiffersLastOrder';

import Exports from 'app/src/components/Exports';
import Financial from 'app/src/components/Financial';
import Alignment from 'app/src/components/Alignment';
import ImageRepeat from 'app/src/components/ImageRepeat';

import FeatureSubscriptions from 'app/src/components/FeatureSubscriptions';
import FeatureSubscriptionSuccess from 'app/src/components/FeatureSubscriptions/Success';

import Makes from 'app/src/components/Makes';

import Notes from 'app/src/components/Notes';
import Note from 'app/src/components/Notes/show';

import Orders from 'app/src/components/Orders';
import NewOrder from 'app/src/components/Orders/new';
import Order from 'app/src/components/Orders/show';
import EditOrder from 'app/src/components/Orders/edit';
import OrderPrintSetup from 'app/src/components/Orders/PrintSetup';
import OtherVendorOrder from 'app/src/components/Orders/OtherVendor';

import QuickbooksAuth from 'app/src/components/Quickbooks/Auth';

import UpcomingReorders from 'app/src/components/Orders/UpcomingReorders';
import ShopifyItems from 'app/src/components/Orders/Shopify';

import Shopify from 'app/src/components/Shopify';
import ShopifyPaymentsWorksheet from 'app/src/components/Shopify/PaymentsWorksheet';

import Supplies from 'app/src/components/Supplies';
import Supply from 'app/src/components/Supplies/show';
import NewSupply from 'app/src/components/Supplies/new';
import PerformInventory from 'app/src/components/Supplies/PerformInventory';

import SystemAlerts from 'app/src/components/SystemAlerts';

import Tags from 'app/src/components/Tags';
import PreRegistrations from 'app/src/components/Tags/PreRegistrations';
import NewTag from 'app/src/components/Tags/new';
import EditTag from 'app/src/components/Tags/edit';
import PrintOverlay from 'app/src/components/Tags/PrintOverlay';
import ResubmitTag from 'app/src/components/Tags/Resubmit';

import ChangePassword from 'app/src/components/Auth/ChangePassword';
import Profile from 'app/src/components/Auth/Profile';
import SetupTOTP from 'app/src/components/Auth/SetupTOTP';
import TwoFactor from 'app/src/components/Auth/TwoFactor';

import Users from 'app/src/components/Users';
import User from 'app/src/components/Users/show';

import Terms from 'app/src/components/Terms';
import Privacy from 'app/src/components/Privacy';

import Docs from 'app/src/components/Docs';
import AllDocs from 'app/src/components/Docs/All';
import DocRoutes from 'app/src/components/Docs/Routes';

import AlertsDocs from 'app/src/components/Docs/Employee/Alerts';
import DataChecksDocs from 'app/src/components/Docs/Employee/DataChecks';

const PrimaryRoutes = () => {
  const { currentUser } = useContext(ClientContext);
  const [searchParams] = useSearchParams();

  // NOTE (Jared E Morgan, 20241230)
  // key on Routes component is a way to force preflight-tables to update due
  // to param/searchParam changes. I'm guessing there's a better way that I
  // should probably build in to preflight-tables.
  return (
    <Routes key={searchParams.get('t') || '0'}>
      <Route exact path="/" element={<Home />} />

      <Route exact path="/terms" element={<Terms />} />
      <Route exact path="/privacy" element={<Privacy />} />

      <Route exact path="/access-tokens" exact element={<AccessTokens />} />
      <Route exact path="/alignment" element={<Alignment />} />

      <Route exact path="/charts">
        <Route exact index element={<Charts />} />
        <Route exact path=":type" element={<Charts />} />
      </Route>

      <Route exact path="/dealers">
        <Route exact index element={<Dealers />} />
        <Route exact path="new" element={<NewDealer />} />
        <Route exact path=":id" element={<Dealer />} />
        <Route exact path=":id/edit" element={<EditDealer />} />
        <Route exact path=":dealerId/charts" element={<Charts />} />
        <Route
          exact
          path=":dealerId/pre-registrations"
          element={<PreRegistrations />}
        />

        {currentUser?.isEmployee && (
          <>
            <Route
              exact
              path="non-adr-pre-registrations"
              element={<DealersNonAdrPreRegistrations />}
            />
            <Route
              exact
              path="no-recent-order"
              element={<DealersNoRecentOrder />}
            />
            <Route
              exact
              path="name-differs"
              element={<DealersNameDiffersLastOrder />}
            />
          </>
        )}
      </Route>

      <Route exact path="/tags">
        <Route exact index element={<Tags />} />
        <Route exact path="register" element={<NewTag />} />
        <Route exact path="register/:serialNumber/:type" element={<NewTag />} />
        <Route
          exact
          path="register/:serialNumber/:type/:dealerId"
          element={<NewTag />}
        />
        <Route exact path=":id/edit" element={<EditTag />} />
        <Route exact path=":id/print-overlay" element={<PrintOverlay />} />
        <Route exact path=":id/resubmit" element={<ResubmitTag />} />
      </Route>
      <Route exact path="/pre-registrations" element={<PreRegistrations />} />

      <Route path="/how-to/*" prefix="" element={<DocRoutes />} />

      <Route exact path="/account/profile" element={<Profile />} />
      <Route exact path="/account/password" element={<ChangePassword />} />
      <Route exact path="/account/totp" element={<SetupTOTP />} />
      <Route exact path="/account/two-factor" element={<TwoFactor />} />

      <Route
        exact
        path="/feature-subscriptions"
        element={<FeatureSubscriptions />}
      />
      <Route
        exact
        path="/feature-subscriptions/success"
        element={<FeatureSubscriptionSuccess />}
      />

      <Route exact path="/users" element={<Users />} />

      <Route exact path="/orders">
        <Route exact index element={<Orders />} />
        <Route exact path=":orderId/tags" element={<Tags />} />
        <Route exact path="other-vendor" element={<OtherVendorOrder />} />

        {currentUser?.isEmployee && (
          <>
            <Route exact path="new" element={<NewOrder />} />
            <Route exact path=":id" element={<Order />} />
            <Route exact path=":id/edit" element={<EditOrder />} />
            <Route exact path=":id/print" element={<OrderPrintSetup />} />
            <Route exact path="shopify" element={<ShopifyItems />} />
            <Route
              exact
              path="upcoming-reorders"
              element={<UpcomingReorders />}
            />
          </>
        )}
      </Route>

      {['admin', 'agency'].includes(currentUser?.role) && (
        <Route exact path="/exports" element={<Exports />} />
      )}

      {'admin' === currentUser?.role && (
        <>
          <Route
            exact
            path="/dealers/approval-status/pending"
            element={<Dealers approvalStatus="Pending" />}
          />

          <Route exact path="/financial" element={<Financial />} />
          <Route exact path="/image-repeat" element={<ImageRepeat />} />

          <Route exact path="/makes">
            <Route exact index element={<Makes />} />
          </Route>

          <Route exact path="/notes">
            <Route exact index element={<Notes />} />
            <Route exact path=":id" element={<Note />} />
          </Route>

          <Route exact path="/quickbooks/auth" element={<QuickbooksAuth />} />

          <Route exact path="/shopify">
            <Route exact index element={<Shopify />} />
            <Route
              exact
              path="payments-worksheet"
              element={<ShopifyPaymentsWorksheet />}
            />
          </Route>

          <Route exact path="/supplies">
            <Route exact index element={<Supplies />} />
            <Route exact path="new" element={<NewSupply />} />
            <Route exact path="inventory" element={<PerformInventory />} />
            <Route exact path=":id" element={<Supply />} />
          </Route>

          <Route exact path="/system-alerts">
            <Route exact index element={<SystemAlerts />} />
          </Route>

          <Route exact path="/users/:id" element={<User />} />
        </>
      )}

      {currentUser?.isEmployee && (
        <>
          <Route path="/employee-docs/alerts" element={<AlertsDocs />} />
          <Route
            path="/employee-docs/data-checks"
            element={<DataChecksDocs />}
          />
        </>
      )}
    </Routes>
  );
};

export default PrimaryRoutes;
